/**
 * Returns & RMA — Return Merchandise Authorization management.
 *
 * Page composition:
 *   - PageHeader + "New Return" action
 *   - KPI strip (Pending, Total value, Approved this month, Avg resolution)
 *   - Filter bar (status, search)
 *   - DataTable
 *   - Detail Sheet on row click
 *   - Create Return Sheet
 *   - Approve / Reject / Issue Credit Note actions
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Plus, RotateCcw, Search, Filter, Check, X, Eye, Receipt, Package, Clock, CheckCircle, Ban, Trash2 } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";

import { formatMoney, formatMoneySmart } from "@bizop/shared/currency";
import {
  Badge, Button, DataTable, Input, KpiCard, Label,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  Sheet, SheetBody, SheetContent, SheetDescription, SheetFooter,
  SheetHeader, SheetTitle, StatusPill, type StatusPillVariant, toast,
} from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { ExportButton } from "../../../components/export";
import { downloadCsv, exportFilename, formatDateForCsv, toCsv, type ExportColumn } from "../../../lib/export";
import { useMockData } from "../../../lib/mockMode";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";
import { mockDealers, type MockDealer } from "../../../lib/salesMock";

/* ── Types ───────────────────────────────────────────────── */

type ReturnStatus = "pending" | "approved" | "received" | "refunded" | "rejected";
type ReturnReason = "defective" | "wrong_item" | "damaged" | "excess" | "other";

type MockReturnItem = {
  productName: string; qty: number; unitPriceCents: bigint; totalCents: bigint;
};

type MockReturn = {
  _id: string; returnNumber: string; dealerId: string;
  invoiceId: string | null; status: ReturnStatus; reason: ReturnReason;
  items: MockReturnItem[]; totalAmountCents: bigint;
  creditNoteId: string | null; notes: string;
  requestedAt: number; resolvedAt: number | null; createdBy: string;
  createdAt: number; updatedAt: number;
};

type MockDealerRecord = MockDealer;

/* ── Constants ───────────────────────────────────────────── */

const NOW = new Date("2026-05-09T08:00:00Z").getTime();
const DAY = 24 * 60 * 60 * 1000;

const STATUS_VARIANTS: Record<ReturnStatus, StatusPillVariant> = {
  pending: "warning", approved: "info", received: "neutral", refunded: "success", rejected: "error",
};
const STATUS_LABELS: Record<ReturnStatus, string> = {
  pending: "Pending", approved: "Approved", received: "Received", refunded: "Refunded", rejected: "Rejected",
};
const REASON_LABELS: Record<ReturnReason, string> = {
  defective: "Defective", wrong_item: "Wrong Item", damaged: "Damaged", excess: "Excess Stock", other: "Other",
};
const ALL_STATUSES: ReturnStatus[] = ["pending", "approved", "received", "refunded", "rejected"];
const ALL_REASONS: ReturnReason[] = ["defective", "wrong_item", "damaged", "excess", "other"];

/* ── Mock data — 8 rows ─────────────────────────────────── */

function ret(
  id: string, num: string, dealer: string, inv: string | null,
  status: ReturnStatus, reason: ReturnReason, items: MockReturnItem[],
  total: bigint, cnId: string | null, notes: string,
  reqAt: number, resAt: number | null,
): MockReturn {
  return {
    _id: id, returnNumber: num, dealerId: dealer, invoiceId: inv,
    status, reason, items, totalAmountCents: total, creditNoteId: cnId,
    notes, requestedAt: reqAt, resolvedAt: resAt, createdBy: "dev-user",
    createdAt: reqAt, updatedAt: resAt ?? reqAt,
  };
}

const mockReturns: MockReturn[] = [
  ret("ret_001", "RET-0001", "dealer_01", "inv_001", "pending", "defective", [
    { productName: "Jasmine Rice 50kg", qty: 5, unitPriceCents: 325000n, totalCents: 1625000n },
    { productName: "Basmati Rice 25kg", qty: 2, unitPriceCents: 275000n, totalCents: 550000n },
  ], 2175000n, null, "Multiple bags torn during transit, rice contaminated.", NOW - 2 * DAY, null),
  ret("ret_002", "RET-0002", "dealer_02", "inv_003", "approved", "wrong_item", [
    { productName: "Mustard Oil 5L", qty: 10, unitPriceCents: 85000n, totalCents: 850000n },
  ], 850000n, null, "Received soybean oil instead of mustard oil.", NOW - 5 * DAY, NOW - 3 * DAY),
  ret("ret_003", "RET-0003", "dealer_03", null, "refunded", "damaged", [
    { productName: "Sugar 50kg", qty: 8, unitPriceCents: 410000n, totalCents: 3280000n },
  ], 3280000n, "cn_ret_003", "Bags water-damaged in warehouse before pickup.", NOW - 14 * DAY, NOW - 10 * DAY),
  ret("ret_004", "RET-0004", "dealer_04", "inv_005", "rejected", "other", [
    { productName: "Turmeric Powder 1kg", qty: 20, unitPriceCents: 18500n, totalCents: 370000n },
  ], 370000n, null, "Return outside 30-day window. Rejected per policy.", NOW - 20 * DAY, NOW - 18 * DAY),
  ret("ret_005", "RET-0005", "dealer_05", "inv_002", "received", "excess", [
    { productName: "Lentils Red 25kg", qty: 12, unitPriceCents: 195000n, totalCents: 2340000n },
    { productName: "Lentils Green 25kg", qty: 6, unitPriceCents: 210000n, totalCents: 1260000n },
  ], 3600000n, null, "Over-ordered by dealer; goods in original condition.", NOW - 7 * DAY, NOW - 4 * DAY),
  ret("ret_006", "RET-0006", "dealer_01", "inv_007", "pending", "defective", [
    { productName: "Flour Atta 10kg", qty: 15, unitPriceCents: 62000n, totalCents: 930000n },
  ], 930000n, null, "Packaging defects on multiple bags; labels peeling.", NOW - 1 * DAY, null),
  ret("ret_007", "RET-0007", "dealer_06", null, "refunded", "damaged", [
    { productName: "Coconut Oil 1L", qty: 24, unitPriceCents: 32000n, totalCents: 768000n },
    { productName: "Coconut Oil 5L", qty: 4, unitPriceCents: 145000n, totalCents: 580000n },
  ], 1348000n, "cn_ret_007", "Bottles cracked during delivery. Full batch returned.", NOW - 21 * DAY, NOW - 16 * DAY),
  ret("ret_008", "RET-0008", "dealer_02", "inv_004", "approved", "wrong_item", [
    { productName: "Chili Powder 500g", qty: 30, unitPriceCents: 9500n, totalCents: 285000n },
  ], 285000n, null, "Received cumin powder instead of chili powder.", NOW - 3 * DAY, NOW - 1 * DAY),
];

/* ── Route ───────────────────────────────────────────────── */

export const Route = createFileRoute("/_app/sales/returns")({
  component: ReturnsPage,
});

/* ── Helpers ─────────────────────────────────────────────── */

function formatDate(ms: number): string {
  return new Date(ms).toLocaleDateString("en-BD", { year: "numeric", month: "short", day: "2-digit" });
}

function nextReturnNumber(existing: MockReturn[]): string {
  let max = 0;
  for (const r of existing) {
    const m = r.returnNumber.match(/RET-(\d+)/);
    if (m && m[1]) { const n = parseInt(m[1], 10); if (n > max) max = n; }
  }
  return `RET-${String(max + 1).padStart(4, "0")}`;
}

/* ── Main page ───────────────────────────────────────────── */

function ReturnsPage() {
  const isMock = useMockData();
  const [rows, setRows] = usePersistentMockState<MockReturn[]>("returns", mockReturns);
  const [dealers] = usePersistentMockState<MockDealerRecord[]>("dealers", mockDealers);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailRow, setDetailRow] = useState<MockReturn | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");

  const dealersById = useMemo(() => new Map(dealers.map((d) => [d._id, d])), [dealers]);

  const filteredRows = useMemo(() => {
    let result = rows;
    if (statusFilter !== "all") result = result.filter((r) => r.status === statusFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter((r) => {
        const dealer = dealersById.get(r.dealerId);
        return r.returnNumber.toLowerCase().includes(q) || (dealer?.displayName ?? "").toLowerCase().includes(q);
      });
    }
    return result;
  }, [rows, statusFilter, searchQuery, dealersById]);

  const kpis = useMemo(() => {
    let pendingCount = 0;
    let totalValue = 0n;
    let approvedThisMonth = 0;
    let resolvedCount = 0;
    let totalResolutionMs = 0;
    const monthStart = new Date("2026-05-01T00:00:00Z").getTime();
    for (const r of rows) {
      if (r.status === "pending") pendingCount++;
      totalValue += r.totalAmountCents;
      if ((r.status === "approved" || r.status === "received" || r.status === "refunded") &&
          r.resolvedAt !== null && r.resolvedAt >= monthStart) approvedThisMonth++;
      if (r.resolvedAt !== null) { resolvedCount++; totalResolutionMs += r.resolvedAt - r.requestedAt; }
    }
    const avgResolutionDays = resolvedCount > 0 ? Math.round(totalResolutionMs / resolvedCount / DAY * 10) / 10 : 0;
    return { pendingCount, totalValue, approvedThisMonth, avgResolutionDays };
  }, [rows]);

  const columns = useMemo<ColumnDef<MockReturn>[]>(() => [
    {
      accessorKey: "returnNumber", header: "Return #",
      cell: ({ row }: { row: { original: MockReturn } }) => (
        <span className="font-mono text-[12px]">{row.original.returnNumber}</span>
      ),
      meta: { mono: true },
    },
    {
      id: "dealer", header: "Dealer",
      cell: ({ row }: { row: { original: MockReturn } }) => (
        <span className="text-(--text-primary)">{dealersById.get(row.original.dealerId)?.displayName ?? "—"}</span>
      ),
    },
    {
      id: "items", header: "Items",
      cell: ({ row }: { row: { original: MockReturn } }) => {
        const count = row.original.items.length;
        const totalQty = row.original.items.reduce((s, i) => s + i.qty, 0);
        return <span className="text-(--text-secondary) text-[13px]">{count} item{count !== 1 ? "s" : ""} ({totalQty} units)</span>;
      },
    },
    {
      id: "amount", header: "Amount",
      cell: ({ row }: { row: { original: MockReturn } }) => (
        <span className="font-mono tabular-nums">
          {formatMoneySmart(row.original.totalAmountCents, "BDT", { locale: "en-BD" })}
        </span>
      ),
      meta: { numeric: true, mono: true },
    },
    {
      id: "reason", header: "Reason",
      cell: ({ row }: { row: { original: MockReturn } }) => (
        <Badge variant="neutral">{REASON_LABELS[row.original.reason]}</Badge>
      ),
      meta: { hideOnMobile: true },
    },
    {
      accessorKey: "status", header: "Status",
      cell: ({ row }: { row: { original: MockReturn } }) => (
        <StatusPill variant={STATUS_VARIANTS[row.original.status]}>{STATUS_LABELS[row.original.status]}</StatusPill>
      ),
    },
    {
      id: "date", header: "Requested",
      cell: ({ row }: { row: { original: MockReturn } }) => (
        <span className="text-(--text-secondary) tabular-nums">{formatDate(row.original.requestedAt)}</span>
      ),
      meta: { hideOnMobile: true },
    },
    {
      id: "actions", header: "",
      cell: ({ row }: { row: { original: MockReturn } }) => (
        <Button variant="ghost" size="sm" onClick={(e: React.MouseEvent) => { e.stopPropagation(); setDetailRow(row.original); }}>
          <Eye className="h-4 w-4" />
        </Button>
      ),
      meta: { className: "w-10" },
    },
  ], [dealersById]);

  /* Actions */

  function handleCreate(values: CreateReturnFormValues) {
    if (!isMock) return;
    const number = nextReturnNumber(rows);
    const items: MockReturnItem[] = values.items.map((item) => ({
      productName: item.productName, qty: item.qty,
      unitPriceCents: BigInt(Math.round(item.unitPrice * 100)),
      totalCents: BigInt(Math.round(item.unitPrice * 100)) * BigInt(item.qty),
    }));
    const total = items.reduce((s, i) => s + i.totalCents, 0n);
    const newReturn: MockReturn = {
      _id: crypto.randomUUID(), returnNumber: number,
      dealerId: values.dealerId, invoiceId: values.invoiceId || null,
      status: "pending", reason: values.reason, items,
      totalAmountCents: total, creditNoteId: null, notes: values.notes,
      requestedAt: Date.now(), resolvedAt: null, createdBy: "dev-user",
      createdAt: Date.now(), updatedAt: Date.now(),
    };
    setRows((prev) => [newReturn, ...prev]);
    setCreateOpen(false);
    toast.success(`Return ${number} created (mock)`);
  }

  function handleApprove(r: MockReturn) {
    setRows((prev) => prev.map((x) => x._id === r._id ? { ...x, status: "approved" as const, resolvedAt: Date.now() } : x));
    setDetailRow(null);
    toast.success(`Return ${r.returnNumber} approved (mock)`);
  }

  function handleReject(r: MockReturn) {
    setRows((prev) => prev.map((x) => x._id === r._id ? { ...x, status: "rejected" as const, resolvedAt: Date.now() } : x));
    setDetailRow(null);
    toast.success(`Return ${r.returnNumber} rejected (mock)`);
  }

  function handleMarkReceived(r: MockReturn) {
    setRows((prev) => prev.map((x) => x._id === r._id ? { ...x, status: "received" as const } : x));
    setDetailRow(null);
    toast.success(`Return ${r.returnNumber} marked as received (mock)`);
  }

  function handleIssueCreditNote(r: MockReturn) {
    const cnId = crypto.randomUUID();
    setRows((prev) => prev.map((x) => x._id === r._id ? { ...x, status: "refunded" as const, creditNoteId: cnId } : x));
    setDetailRow(null);
    toast.success(`Credit note issued for return ${r.returnNumber} (mock)`);
  }

  function handleDelete(r: MockReturn) {
    setRows((prev) => prev.filter((x) => x._id !== r._id));
    setDetailRow(null);
    toast.success(`Return ${r.returnNumber} deleted (mock)`);
  }

  /* CSV Export */

  function handleExportCsv(extension: "csv" | "xls") {
    const cols: ExportColumn<MockReturn>[] = [
      { key: "returnNumber", label: "Return #" },
      { key: "dealerId", label: "Dealer", value: (r) => dealersById.get(r.dealerId)?.displayName ?? r.dealerId },
      { key: "reason", label: "Reason", format: (v) => REASON_LABELS[String(v) as ReturnReason] ?? String(v) },
      { key: "totalAmountCents", label: "Amount (BDT)" },
      { key: "status", label: "Status", format: (v) => STATUS_LABELS[v as ReturnStatus] ?? String(v) },
      { key: "requestedAt", label: "Requested", format: (v) => formatDateForCsv(Number(v)) },
      { key: "resolvedAt", label: "Resolved", format: (v) => formatDateForCsv(v as number | null | undefined) },
      { key: "notes", label: "Notes" },
    ];
    downloadCsv(exportFilename("returns", extension), toCsv(filteredRows, cols));
  }

  /* Render */

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Returns & RMA"
        description="Manage return merchandise authorizations, approve or reject returns, and issue credit notes."
        actions={
          <>
            <ExportButton onCsv={() => handleExportCsv("csv")} onExcel={() => handleExportCsv("xls")} />
            <Button onClick={() => setCreateOpen(true)}><Plus className="h-4 w-4" /> New Return</Button>
          </>
        }
      />

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" aria-label="Returns summary">
        <KpiCard caption="Pending Returns" value={String(kpis.pendingCount)} hint="Awaiting review" />
        <KpiCard caption="Total Returns Value" value={formatMoneySmart(kpis.totalValue, "BDT", { locale: "en-BD" })} hint="All-time return value" />
        <KpiCard caption="Approved (this month)" value={String(kpis.approvedThisMonth)} hint="May 2026" />
        <KpiCard caption="Avg Resolution Time" value={`${kpis.avgResolutionDays}d`} hint="Request to resolution" />
      </section>

      <section className="flex flex-wrap items-end gap-3">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-(--text-tertiary)" aria-hidden />
          <Select value={statusFilter} onValueChange={(v: string) => setStatusFilter(v)}>
            <SelectTrigger className="w-[160px]"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {ALL_STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="relative flex-1 min-w-[200px] max-w-[320px]">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-(--text-tertiary)" aria-hidden />
          <Input placeholder="Search return # or dealer..." value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)} className="pl-9" />
        </div>
      </section>

      <DataTable
        data={filteredRows} columns={columns}
        getRowId={(r: MockReturn) => r._id}
        onRowClick={(row: { original: MockReturn }) => setDetailRow(row.original)}
        emptyTitle="No returns" emptyDescription="No return requests match your filters."
        emptyIcon={<RotateCcw className="h-5 w-5" aria-hidden />}
        emptyAction={<Button onClick={() => setCreateOpen(true)}><Plus className="h-4 w-4" /> New Return</Button>}
      />

      <Sheet open={createOpen} onOpenChange={(open: boolean) => setCreateOpen(open)}>
        <SheetContent side="right" size="md">
          <SheetHeader>
            <SheetTitle>New Return</SheetTitle>
            <SheetDescription>Create a return merchandise authorization request.</SheetDescription>
          </SheetHeader>
          <SheetBody>
            <CreateReturnForm dealers={dealers} onSubmit={handleCreate} onCancel={() => setCreateOpen(false)} />
          </SheetBody>
        </SheetContent>
      </Sheet>

      <Sheet open={detailRow !== null} onOpenChange={(o: boolean) => { if (!o) setDetailRow(null); }}>
        <SheetContent side="right" size="md">
          {detailRow ? (
            <ReturnDetail row={detailRow} dealersById={dealersById}
              onApprove={() => handleApprove(detailRow)} onReject={() => handleReject(detailRow)}
              onMarkReceived={() => handleMarkReceived(detailRow)}
              onIssueCreditNote={() => handleIssueCreditNote(detailRow)}
              onDelete={() => handleDelete(detailRow)} />
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

/* ── Detail component ────────────────────────────────────── */

function ReturnDetail({ row, dealersById, onApprove, onReject, onMarkReceived, onIssueCreditNote, onDelete }: {
  row: MockReturn;
  dealersById: Map<string, MockDealerRecord>;
  onApprove: () => void;
  onReject: () => void;
  onMarkReceived: () => void;
  onIssueCreditNote: () => void;
  onDelete: () => void;
}) {
  const dealer = dealersById.get(row.dealerId) ?? null;

  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-3">
          <span className="font-mono">{row.returnNumber}</span>
          <StatusPill variant={STATUS_VARIANTS[row.status]}>{STATUS_LABELS[row.status]}</StatusPill>
        </SheetTitle>
        <SheetDescription>{dealer?.displayName ?? "—"}</SheetDescription>
      </SheetHeader>
      <SheetBody>
        <div className="flex flex-col gap-5">
          <section className="grid grid-cols-2 gap-3">
            <DetailField label="Amount" value={formatMoney(row.totalAmountCents, "BDT", { locale: "en-BD", display: "code" })} />
            <DetailField label="Reason" value={REASON_LABELS[row.reason]} />
            <DetailField label="Requested" value={formatDate(row.requestedAt)} />
            <DetailField label="Resolved" value={row.resolvedAt ? formatDate(row.resolvedAt) : "Pending"} />
            <DetailField label="Invoice" value={row.invoiceId ? <span className="font-mono">{row.invoiceId}</span> : "None"} />
            <DetailField label="Credit Note" value={row.creditNoteId ? <span className="font-mono">{row.creditNoteId}</span> : "Not issued"} />
          </section>

          <section>
            <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-2">Return Items</div>
            <div className="border border-(--border-subtle) rounded-md overflow-hidden">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="border-b border-(--border-subtle) bg-(--bg-muted)">
                    <th className="text-left px-3 py-2 font-medium text-(--text-secondary)">Product</th>
                    <th className="text-right px-3 py-2 font-medium text-(--text-secondary)">Qty</th>
                    <th className="text-right px-3 py-2 font-medium text-(--text-secondary)">Unit Price</th>
                    <th className="text-right px-3 py-2 font-medium text-(--text-secondary)">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {row.items.map((item, idx) => (
                    <tr key={idx} className="border-b border-(--border-subtle) last:border-b-0">
                      <td className="px-3 py-2 text-(--text-primary)">{item.productName}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-(--text-secondary)">{item.qty}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-(--text-secondary)">
                        {formatMoneySmart(item.unitPriceCents, "BDT", { locale: "en-BD" })}
                      </td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-(--text-primary)">
                        {formatMoneySmart(item.totalCents, "BDT", { locale: "en-BD" })}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-(--bg-muted)">
                    <td colSpan={3} className="px-3 py-2 text-right font-medium text-(--text-primary)">Total</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums font-semibold text-(--text-primary)">
                      {formatMoney(row.totalAmountCents, "BDT", { locale: "en-BD", display: "code" })}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>

          {row.notes ? (
            <section>
              <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-1">Notes</div>
              <p className="text-[13px] text-(--text-secondary) whitespace-pre-line">{row.notes}</p>
            </section>
          ) : null}

          <section>
            <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-2">Timeline</div>
            <div className="flex flex-col gap-2">
              <TimelineEntry icon={<Clock className="h-3.5 w-3.5" />} label="Requested" date={formatDate(row.requestedAt)} />
              {row.status === "approved" || row.status === "received" || row.status === "refunded" ? (
                <TimelineEntry icon={<CheckCircle className="h-3.5 w-3.5 text-green-600" />} label="Approved" date={row.resolvedAt ? formatDate(row.resolvedAt) : "—"} />
              ) : null}
              {row.status === "received" || row.status === "refunded" ? (
                <TimelineEntry icon={<Package className="h-3.5 w-3.5 text-blue-600" />} label="Goods received" date={row.resolvedAt ? formatDate(row.resolvedAt) : "—"} />
              ) : null}
              {row.status === "refunded" ? (
                <TimelineEntry icon={<Receipt className="h-3.5 w-3.5 text-green-600" />} label="Credit note issued" date={row.resolvedAt ? formatDate(row.resolvedAt) : "—"} />
              ) : null}
              {row.status === "rejected" ? (
                <TimelineEntry icon={<Ban className="h-3.5 w-3.5 text-red-600" />} label="Rejected" date={row.resolvedAt ? formatDate(row.resolvedAt) : "—"} />
              ) : null}
            </div>
          </section>
        </div>
      </SheetBody>
      <SheetFooter>
        {row.status === "pending" ? (
          <>
            <Button onClick={onApprove}><Check className="h-4 w-4" /> Approve</Button>
            <Button variant="destructive" onClick={onReject}><X className="h-4 w-4" /> Reject</Button>
          </>
        ) : null}
        {row.status === "approved" ? (
          <>
            <Button onClick={onMarkReceived}><Package className="h-4 w-4" /> Mark Received</Button>
            <Button variant="outline" onClick={onIssueCreditNote}><Receipt className="h-4 w-4" /> Issue Credit Note</Button>
          </>
        ) : null}
        {row.status === "received" ? (
          <Button onClick={onIssueCreditNote}><Receipt className="h-4 w-4" /> Issue Credit Note</Button>
        ) : null}
        {row.status === "pending" || row.status === "rejected" ? (
          <Button variant="ghost" onClick={onDelete}><Trash2 className="h-4 w-4" /> Delete</Button>
        ) : null}
      </SheetFooter>
    </>
  );
}

/* ── Timeline entry ──────────────────────────────────────── */

function TimelineEntry({ icon, label, date }: { icon: React.ReactNode; label: string; date: string }) {
  return (
    <div className="flex items-center gap-2 text-[13px]">
      <span className="text-(--text-tertiary)">{icon}</span>
      <span className="text-(--text-primary) font-medium">{label}</span>
      <span className="text-(--text-tertiary) tabular-nums ml-auto">{date}</span>
    </div>
  );
}

/* ── Detail field ────────────────────────────────────────── */

function DetailField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">{label}</div>
      <div className="text-[13px] text-(--text-primary) font-mono tabular-nums">{value}</div>
    </div>
  );
}

/* ── Create Return form ──────────────────────────────────── */

type CreateReturnFormValues = {
  dealerId: string; invoiceId: string; reason: ReturnReason;
  notes: string; items: Array<{ productName: string; qty: number; unitPrice: number }>;
};

type FormItemRow = { productName: string; qty: string; unitPrice: string };

function CreateReturnForm({ dealers, onSubmit, onCancel }: {
  dealers: MockDealerRecord[];
  onSubmit: (values: CreateReturnFormValues) => void;
  onCancel: () => void;
}) {
  const [dealerId, setDealerId] = useState("");
  const [invoiceId, setInvoiceId] = useState("");
  const [reason, setReason] = useState<ReturnReason>("defective");
  const [notes, setNotes] = useState("");
  const [formItems, setFormItems] = useState<FormItemRow[]>([{ productName: "", qty: "1", unitPrice: "" }]);

  function addItem() {
    setFormItems((prev) => [...prev, { productName: "", qty: "1", unitPrice: "" }]);
  }
  function removeItem(idx: number) {
    setFormItems((prev) => prev.filter((_, i) => i !== idx));
  }
  function updateItem(idx: number, field: keyof FormItemRow, value: string) {
    setFormItems((prev) => prev.map((item, i) => (i === idx ? { ...item, [field]: value } : item)));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!dealerId) { toast.error("Please select a dealer."); return; }
    const parsedItems = formItems
      .filter((item) => item.productName.trim())
      .map((item) => ({
        productName: item.productName.trim(),
        qty: parseInt(item.qty, 10) || 1,
        unitPrice: parseFloat(item.unitPrice) || 0,
      }));
    if (parsedItems.length === 0) { toast.error("Please add at least one return item."); return; }
    onSubmit({ dealerId, invoiceId, reason, notes, items: parsedItems });
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="return-dealer">Dealer</Label>
        <Select value={dealerId} onValueChange={(v: string) => setDealerId(v)}>
          <SelectTrigger id="return-dealer"><SelectValue placeholder="Select dealer..." /></SelectTrigger>
          <SelectContent>
            {dealers.map((d) => <SelectItem key={d._id} value={d._id}>{d.displayName}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="return-invoice">Invoice ID (optional)</Label>
        <Input id="return-invoice" placeholder="e.g. inv_001" value={invoiceId}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInvoiceId(e.target.value)} />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="return-reason">Reason</Label>
        <Select value={reason} onValueChange={(v: string) => setReason(v as ReturnReason)}>
          <SelectTrigger id="return-reason"><SelectValue placeholder="Select reason..." /></SelectTrigger>
          <SelectContent>
            {ALL_REASONS.map((r) => <SelectItem key={r} value={r}>{REASON_LABELS[r]}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <Label>Return Items</Label>
          <Button type="button" variant="ghost" size="sm" onClick={addItem}>
            <Plus className="h-3.5 w-3.5" /> Add item
          </Button>
        </div>
        {formItems.map((item, idx) => (
          <div key={idx} className="grid grid-cols-[1fr_60px_90px_32px] gap-2 items-end">
            <div className="flex flex-col gap-1">
              {idx === 0 ? <span className="text-[11px] text-(--text-tertiary)">Product</span> : null}
              <Input placeholder="Product name" value={item.productName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateItem(idx, "productName", e.target.value)} />
            </div>
            <div className="flex flex-col gap-1">
              {idx === 0 ? <span className="text-[11px] text-(--text-tertiary)">Qty</span> : null}
              <Input type="number" min="1" value={item.qty}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateItem(idx, "qty", e.target.value)} />
            </div>
            <div className="flex flex-col gap-1">
              {idx === 0 ? <span className="text-[11px] text-(--text-tertiary)">Price</span> : null}
              <Input type="number" step="0.01" placeholder="0.00" value={item.unitPrice}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateItem(idx, "unitPrice", e.target.value)} />
            </div>
            <div>
              {formItems.length > 1 ? (
                <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(idx)}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              ) : <div className="w-8" />}
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="return-notes">Notes</Label>
        <Input id="return-notes" placeholder="Reason details, condition of goods..." value={notes}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNotes(e.target.value)} />
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit"><Plus className="h-4 w-4" /> Create Return</Button>
      </div>
    </form>
  );
}
